/* eslint-disable camelcase */
import { Audio, Image, RiArrowLeftSLine, RiArrowRightSLine, Stack, StackProps, Video } from 'component-library';
import { cn } from 'component-library/utils';
import { ComponentRef, CSSProperties, ReactNode, useRef, useState } from 'react';

import { PLOT_MEDIA_FILE_TYPE, PlotOverviewMediaFile } from '@/api/rest/resources/types/plot';
import { DialogBodyWidget, DialogContentWidget, DialogLayoutWidget } from '@/components/widgets/o/Dialog/Dialog';
import { prefersReducedMotion } from '@/utils/motionPreference';

const SETTINGS = {
  [PLOT_MEDIA_FILE_TYPE.IMAGE]: {
    WIDTH: 310, // px
    HEIGHT: 170, // px
  },
  [PLOT_MEDIA_FILE_TYPE.VIDEO]: {
    WIDTH: 310, // px
    HEIGHT: 200, // px
  },
  [PLOT_MEDIA_FILE_TYPE.AUDIO]: {
    WIDTH: 310, // px
    HEIGHT: 50, // px
  },
  DIALOG_SIZE: 80, // vw and vh
} as const;

type CarouselDirection = 'left' | 'right';

export const MediaFiles = ({ mediaFiles }: { mediaFiles: PlotOverviewMediaFile[] }) => {
  const images = mediaFiles.filter((file) => file.type === PLOT_MEDIA_FILE_TYPE.IMAGE);
  const videos = mediaFiles.filter((file) => file.type === PLOT_MEDIA_FILE_TYPE.VIDEO);
  const audios = mediaFiles.filter((file) => file.type === PLOT_MEDIA_FILE_TYPE.AUDIO);

  return (
    <Stack spacing={10} className='my-4'>
      <Carousel files={images} type={PLOT_MEDIA_FILE_TYPE.IMAGE}>
        {images.map((image) => (
          <MediaImage key={image.id} {...image} />
        ))}
      </Carousel>
      <Carousel files={videos} type={PLOT_MEDIA_FILE_TYPE.VIDEO}>
        {videos.map((video) => (
          <MediaVideo key={video.id} {...video} />
        ))}
      </Carousel>
      <Carousel files={audios} type={PLOT_MEDIA_FILE_TYPE.AUDIO}>
        {audios.map((audio) => (
          <MediaAudio key={audio.id} {...audio} />
        ))}
      </Carousel>
    </Stack>
  );
};

const CarouselButton = ({
  direction,
  onClick,
}: {
  direction: CarouselDirection;
  onClick: (direction: CarouselDirection) => void;
}) => {
  return (
    <button
      onClick={() => onClick(direction)}
      className={cn(
        'flex h-8 w-8 items-center justify-center',
        'rounded-full border border-primary-100 hover:bg-primary-12',
      )}
    >
      {direction === 'left' ? <RiArrowLeftSLine /> : <RiArrowRightSLine />}
    </button>
  );
};

type CarouselProps = StackProps & {
  type: (typeof PLOT_MEDIA_FILE_TYPE)[keyof typeof PLOT_MEDIA_FILE_TYPE];
  files: PlotOverviewMediaFile[];
};

export const Carousel = ({ className, children, type, files, ...delegated }: CarouselProps) => {
  const carouselRef = useRef<ComponentRef<typeof Stack>>(null);

  if (files.length === 0) {
    return null;
  }

  if (files.length === 1) {
    return <Stack className={cn('items-center', className)}>{children}</Stack>;
  }

  const scrollCarousel = (direction: CarouselDirection) => {
    carouselRef.current?.scrollBy({
      left: direction === 'left' ? 0 - SETTINGS[`${type}`].WIDTH : SETTINGS[`${type}`].WIDTH,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  return (
    <Stack spacing={4}>
      <Stack
        ref={carouselRef}
        direction='row'
        spacing={4}
        className={cn('min-w-0 overflow-x-auto whitespace-nowrap pb-2 [scrollbar-width:_thin]', className)}
        {...delegated}
      >
        {children}
      </Stack>
      <Stack direction='row' spacing={2.5} className='justify-center'>
        <CarouselButton direction='left' onClick={scrollCarousel} />
        <CarouselButton direction='right' onClick={scrollCarousel} />
      </Stack>
    </Stack>
  );
};

type MediaFileProps = { className?: string } & PlotOverviewMediaFile;

export const MediaImage = ({ caption, name, url, className }: MediaFileProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <FigureContainer
        spacing={4}
        data-testid='media-image'
        onClick={() => setIsOpen(true)}
        className={cn('cursor-pointer', className)}
        mediaType={PLOT_MEDIA_FILE_TYPE.IMAGE}
      >
        <Image
          src={url}
          alt={`Image for plot media: ${name}`}
          style={
            {
              minWidth: `${SETTINGS[PLOT_MEDIA_FILE_TYPE.IMAGE].WIDTH}px`,
              maxWidth: `${SETTINGS[PLOT_MEDIA_FILE_TYPE.IMAGE].WIDTH}px`,
              minHeight: `${SETTINGS[PLOT_MEDIA_FILE_TYPE.IMAGE].HEIGHT}px`,
              maxHeight: `${SETTINGS[PLOT_MEDIA_FILE_TYPE.IMAGE].HEIGHT}px`,
            } as CSSProperties
          }
          className='rounded-3xl object-cover'
        />
        <FigureFooter caption={caption} name={name} />
      </FigureContainer>
      <ImageDialog url={url} name={name} isOpen={isOpen} onOpenChange={setIsOpen} />
    </>
  );
};

export const MediaVideo = ({ caption, name, url, className }: MediaFileProps) => {
  return (
    <FigureContainer className={className} data-testid='media-video' mediaType={PLOT_MEDIA_FILE_TYPE.VIDEO}>
      <Video
        url={url}
        width={SETTINGS[PLOT_MEDIA_FILE_TYPE.VIDEO].WIDTH}
        height={SETTINGS[PLOT_MEDIA_FILE_TYPE.VIDEO].HEIGHT}
        className='rounded-md'
      />
      <FigureFooter caption={caption} name={name} />
    </FigureContainer>
  );
};

export const MediaAudio = ({ caption, name, url, className, thumbnail_url }: MediaFileProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <FigureContainer
        data-testid='media-audio'
        className={cn('rounded-3xl p-3 shadow-md bg-neutral-black-4', className)}
        mediaType={PLOT_MEDIA_FILE_TYPE.AUDIO}
        style={{
          width: thumbnail_url
            ? `calc(${SETTINGS[PLOT_MEDIA_FILE_TYPE.AUDIO].WIDTH}px + 5.75rem)` // 5 + 0.75rem = img width + FigureContainer padding
            : SETTINGS[PLOT_MEDIA_FILE_TYPE.AUDIO].WIDTH,
        }}
      >
        <Stack direction='row' spacing={4} className='items-center'>
          {thumbnail_url && (
            <Image
              src={thumbnail_url}
              alt={`Thumbnail for audio media: ${name}`}
              className='rounded-3xl object-cover cursor-pointer aspect-square'
              onClick={() => setIsOpen(true)}
              style={{
                maxHeight: `5rem`,
              }}
            />
          )}
          <Stack>
            {name && <FigureName name={name} className='text-sm text-start ml-4' />}
            {caption && <FigureCaption caption={caption} className='text-start px-0 ml-4' />}
            <Audio url={url} />
          </Stack>
        </Stack>
      </FigureContainer>
      {thumbnail_url && <ImageDialog url={thumbnail_url} name={name} isOpen={isOpen} onOpenChange={setIsOpen} />}
    </>
  );
};

const ImageDialog = ({
  url,
  name,
  isOpen,
  onOpenChange,
}: {
  url: string;
  name?: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}) => {
  return (
    <DialogLayoutWidget open={isOpen} onOpenChange={onOpenChange}>
      <DialogContentWidget
        withXButton={true}
        className={`h-[${SETTINGS.DIALOG_SIZE}vh] w-[${SETTINGS.DIALOG_SIZE}vw] max-w-[${SETTINGS.DIALOG_SIZE}vw] max-h-[${SETTINGS.DIALOG_SIZE}vh]`}
      >
        <DialogBodyWidget className='flex items-center justify-center h-full w-full' data-testid='media-image-dialog'>
          <Image
            src={url}
            alt={`Full size image for plot media: ${name}`}
            className='object-contain w-[calc(var(--img-width)_*_1px)]'
            style={{
              maxHeight: `calc(${SETTINGS.DIALOG_SIZE}vh - 5rem)`, // -5rem due to the py-10 of the dialog component (otherwise the img overflows)
              maxWidth: `calc(${SETTINGS.DIALOG_SIZE}vw - 6rem)`, // -6rem due to the px-12 of the dialog component (otherwise the img overflows)
            }}
          />
        </DialogBodyWidget>
      </DialogContentWidget>
    </DialogLayoutWidget>
  );
};

const FigureContainer = ({
  children,
  className,
  mediaType,
  ...delegated
}: {
  children: ReactNode;
  className?: string;
  mediaType: (typeof PLOT_MEDIA_FILE_TYPE)[keyof typeof PLOT_MEDIA_FILE_TYPE];
} & StackProps) => {
  return (
    <Stack
      spacing={4}
      className={cn('w-fit', className)}
      style={{ maxWidth: SETTINGS[`${mediaType}`].WIDTH }}
      asChild
      {...delegated}
    >
      <figure>{children}</figure>
    </Stack>
  );
};

const FigureFooter = ({ caption, name, className }: { caption?: string; name?: string; className?: string }) => {
  return (
    <Stack className={cn('w-fit m-auto', className)}>
      {name && <FigureName name={name} />}
      {caption && <FigureCaption caption={caption} />}
    </Stack>
  );
};

const FigureName = ({ name, className }: { name: string; className?: string }) => {
  return (
    <figcaption
      className={cn(
        'typography-body1 font-semibold break-normal text-center text-text-secondary [text-wrap-mode:_wrap]',
        className,
      )}
    >
      {name}
    </figcaption>
  );
};

const FigureCaption = ({ caption, className }: { caption: string; className?: string }) => {
  return (
    <figcaption
      className={cn(
        'typography-body2 break-normal px-3 text-center text-text-secondary [text-wrap-mode:_wrap]',
        className,
      )}
    >
      {caption}
    </figcaption>
  );
};
